import scrollparent from 'scrollparent';
import StickyObserver from 'sticky-observer';
import type { App } from 'vue';

const observers = new Map<Element, { observer: StickyObserver; root?: Element | Document }>();
const OFFSET_TRANSITION_DURATION_VAR = '--sticky-offset-transition-duration';
const DEFAULT_OFFSET_TRANSITION_DURATION = 400;

let resumeTimeout = 0;
let resumeFrame = 0;
let resizeFrame = 0;
let settleFrame = 0;
let paused = false;

window.addEventListener('resize', refreshOnNextResizeFrame, { passive: true });

export function refreshSticky() {
    if (paused) return;

    observers.forEach(({ observer }) => observer.start());
}

export function refreshStickyAfterOffsetChange() {
    if (resumeTimeout) {
        clearTimeout(resumeTimeout);
        resumeTimeout = 0;
    }

    if (resumeFrame) {
        cancelAnimationFrame(resumeFrame);
        resumeFrame = 0;
    }

    paused = true;
    observers.forEach(({ observer }) => observer.stop());

    resumeTimeout = window.setTimeout(() => {
        resumeTimeout = 0;

        resumeFrame = requestAnimationFrame(() => {
            resumeFrame = 0;
            paused = false;
            refreshSticky();
        });
    }, offsetTransitionDuration());
}

export function registerStickyDirective(app: App) {
    app.directive('sticky-observer', {
        mounted(el) {
            if (!el.parentNode) return;

            const parent = scrollparent(el) || undefined;
            const root = parent === document.documentElement ? document : parent;
            const observer = new StickyObserver(
                el,
                (stuck) => el.classList.toggle('is-stuck', stuck),
                { root },
            );

            observers.set(el, { observer, root });

            if (paused) {
                observer.stop();
            }

            if (root === document) settleSticky();
        },

        unmounted(el) {
            observers.get(el)?.observer.stop();
            observers.delete(el);
        },
    });
}

/**
 * Mark which of the page's sticky bars are stuck, before the next paint.
 *
 * Observers only report after a paint, so a bar that just appeared, or that a
 * navigation scrolled, would look wrong for a frame. This waits for the next
 * frame, so it runs after the router scrolls.
 */
export function settleSticky() {
    if (settleFrame) return;

    settleFrame = requestAnimationFrame(() => {
        settleFrame = 0;

        // Read every bar's position before changing classes, so layout runs
        // once.
        const bars = [...observers].filter(([, { root }]) => root === document).map(([el]) => el);
        const height = document.documentElement.clientHeight;
        const stuck = bars.map((el) => {
            const style = getComputedStyle(el);
            const rect = el.getBoundingClientRect();
            // The side a bar doesn't stick to has an auto offset, which parses
            // as NaN and fails its comparison.
            return (
                rect.top <= parseFloat(style.top) + 0.5 ||
                rect.bottom >= height - parseFloat(style.bottom) - 0.5
            );
        });
        bars.forEach((el, i) => el.classList.toggle('is-stuck', stuck[i]));
    });
}

function refreshOnNextResizeFrame() {
    if (resizeFrame) return;

    resizeFrame = requestAnimationFrame(() => {
        resizeFrame = 0;
        refreshSticky();
    });
}

function offsetTransitionDuration() {
    const duration = getComputedStyle(document.documentElement)
        .getPropertyValue(OFFSET_TRANSITION_DURATION_VAR)
        .trim();
    const value = parseFloat(duration);

    if (!Number.isFinite(value)) {
        return DEFAULT_OFFSET_TRANSITION_DURATION;
    }

    return duration.endsWith('ms') ? value : value * 1000;
}
